import "./bootstrap";

import Vue from "vue";
import { mapGetters, mapActions, mapMutations } from "vuex";
import { TuLogger } from "@tinyurldev/tu-ui-vue";

import { getFingerprint } from "@thumbmarkjs/thumbmarkjs";
import { isNil, merge } from "lodash-es";

import { PiniaVuePlugin } from "pinia";

import i18n from "@Resources/js/i18n";
import pinia from "@Resources/js/store/pinia";
import router from "@Resources/js/router";
import store from "@Resources/js/store";

import HlpLazyLoadModal from "@Helpers/functions/lazyLoadModal";
import { logError, logWarning, logInfo } from "@Helpers/logger";
import { XUserTeamStore } from "@Modules/teams/Store/XUserTeamStore.ts";
import { XTeamsStore } from "@Modules/teams/Store/XTeamsStore";
import { XShoppingCartStore } from "@Modules/shoppingcart/Store/XShoppingCartStore";
import { XTeamInvitationsStore } from "@Modules/teams/Store/XTeamInvitationsStore";
import { XOrganizationInvitationsStore } from "@Modules/teams/Store/XOrganizationInvitationsStore";

import MixinBfcm from "@Mixins/bfcm2023";
import MixinFreetrial from "@Mixins/freetrial";
import MixinSubscriptions from "@Mixins/subscriptions";

import useAuth from "@Composables/useAuth";
import usePaddle from "@Composables/usePaddle";

Vue.use(PiniaVuePlugin);
import LaravelBladeVueBridge from "@Resources/js/LaravelBladeVueBridge.vue";

new Vue({
  i18n,
  pinia,
  router,
  store,

  el: "#tinyurl",

  mixins: [MixinBfcm, MixinFreetrial, MixinSubscriptions],

  setup() {
    const { doLogout } = useAuth();
    const { initPaddle, updatePaddleCustomer } = usePaddle();
    const cartStore = XShoppingCartStore();
    const userTeamStore = XUserTeamStore();
    const teamInvitationsStore = XTeamInvitationsStore();
    const organizationInvitationsStore = XOrganizationInvitationsStore();

    return {
      doLogout,
      initPaddle,
      updatePaddleCustomer,
      cartStore,
      userTeamStore,
      teamInvitationsStore,
      organizationInvitationsStore,
    };
  },

  data: () => ({
    twoFactorModalOpened: false,
  }),

  computed: {
    ...mapGetters([
      "getUserCurrentSubscription",
      "isUserSubscribed",
      "locale",
      "notifications",
      "hasBackupCodes",
      "isTwoFactorEnabled",
      "isUserLoggedIn",
      "user",
      "commonDomains",
    ]),
  },

  watch: {
    getUserCurrentSubscription() {
      if (this.shouldTrialReminderModalAppear) {
        this.showTrialReminderModal();
      }
    },

    isUserLoggedIn() {
      this.updatePaddleCustomer();

      if (this.isUserLoggedIn) {
        // load required data when logging in
        this.teamInvitationsStore.initializeAndShowPopup();
        this.organizationInvitationsStore.initializeAndShowPopup();
        this.loadNotifications();

        // Cart initialization is handled by router.afterEach in router/index.js
        // This ensures the route is fully resolved before checking org context
      } else {
        // perform a clean up when logging out
        this.SET_ANNOUNCEMENTS([]);
        this.SET_NOTIFICATIONS([]);
        this.teamInvitationsStore.reset();
        this.organizationInvitationsStore.reset();
      }
    },

    user() {
      this.check2FABackupCodesCount();
      this.prepareUserSubscriptions(); // reinitialize subscriptions so trial flags will be reset
      this.setupCurrentSubscription();
    },

    $route(to) {
      this.check2FABackupCodesCount();
      this.redirectPastDueUsers(to);
    },

    locale(to) {
      this.switchUILanguage(to);
      this.switchMomentLanguage(to);
    },
  },

  async created() {
    this.setupLibraryLogger();
    this.createModalEventListener();
    this.initializeStripe();
    this.initPaddle();
    await this.setupSessionPrint();
    await this.refreshState();
    this.removeLoader();
    this.setupBfcmVisibility();
    this.setupHeartbeat();
  },

  mounted() {
    if (window.error !== undefined) {
      Bus.$emit("showModal", HlpLazyLoadModal("Error"), window.error);
    }

    window.addEventListener("storage", (event) => {
      if (event.key === "stateStatus") {
        if (["false", false].includes(event.newValue)) {
          this.doLogout();
        }

        if (["true", true].includes(event.newValue)) {
          this.refreshState().then(() => {
            // these 2-based redirection will perform a navigation change
            // taking the user to trap/expired page if past_due
            this.$router.push({ name: "settings.profile" }).then(() => {
              this.$router.push({ name: "homepage" });
            });
          });
        }
      }
    });
  },

  methods: {
    ...mapMutations([
      "SET_ANNOUNCEMENTS",
      "SET_CURRENT_SUBSCRIPTION",
      "SET_NOTIFICATIONS",
    ]),

    ...mapActions([
      "loadNotifications",
      "initializeStripe",
      "refreshState",
    ]),

    check2FABackupCodesCount() {
      if (this.twoFactorModalOpened) return;

      if (!this.$route?.name) return;
      if (this.$route?.name?.startsWith("org.")) return;
      if (XUserTeamStore().isTeamsAllowed) return;

      if (this.isTwoFactorEnabled && !this.hasBackupCodes) {
        Bus.$emit(
          "showModal",
          HlpLazyLoadModal("2fa/NoBackupCodes"),
          {},
          {},
          {
            opened: () => (this.twoFactorModalOpened = true),
            closed: () => (this.twoFactorModalOpened = false),
          },
        );
      }
    },

    /**
     * Listens for the "showModal" global event bus and executes the provided callback.
     *
     * @param {string} callback.component - The component to show in the modal.
     * @param {Object} [callback.props={}] - Additional properties to be passed on to the modal component.
     * @param {Object} [callback.params={}] - Parameters for configuring the modal.
     * @param {Object} [callback.events={}] - Event listeners for the modal.
     * @param {boolean} [callback.once=false] - The modal will only open if there is no existing modal currently rendered, if true.
     *
     * @returns {void} - Nothing will be returned but a modal will open unless component cannot be found.
     */
    createModalEventListener() {
      Bus.$on(
        "showModal",
        (component, props = {}, params = {}, events = {}, once = false) => {
          if (!component) {
            console.error("The showModal component is missing!");
            return;
          }

          const defaultParams = {
            adaptive: true,
            scrollable: true,
            clickToClose: false,
            height: "auto",
          };

          events = {
            ...(once && {
              "before-open": (e) => {
                const domModal = document.querySelector(".modal");
                const isSomeModalOpen = !isNil(domModal);

                // cancel the modal if there is an open modal running
                if (isSomeModalOpen) e.cancel();
              },
            }),
            ...events,
          };

          this.$modal.show(
            component,
            props,
            merge({}, defaultParams, params),
            events,
          );
        },
      );
    },

    removeLoader() {
      // remove loader after state has been loaded
      const mainLoader = document.getElementById("mainLoader");
      mainLoader.remove();
    },

    setupBfcmVisibility() {
      if (this.isUserLoggedIn) {
        // save if user subscribed to the browser for BFCM promo
        localStorage.setItem("isBfcmPromoVisible", !this.isUserSubscribed);
      }
    },

    /**
    * We periodically perform a heartbeat to the server to keep session/CSRF alive and also to fetch 
    * notifications if you are a logged in user. These requests hit cloudflare and are actually
    * quite expensive in aggregate, so to limit cost, the time interval in which we perform 
    * this depends on whether you are logged in and whether this application is the 
    * active tab in your browser. 
    */ 
    setupHeartbeat() {
      const oneMinute = 60 * 1000;
      const loggedInUserInterval = oneMinute * 2;
      const keepAliveIfActiveTab = oneMinute * 29;
      const keepAliveIfNotActiveTab = oneMinute * 59;

      // Logged in users don't need heartbeats, fetching notifications will accomplish the same goal
      let lastNotifications = Date.now();
      const refreshNotifications = () => {
        if (document.hidden || !this.isUserLoggedIn) {
          return;
        }
        if (Date.now() - lastNotifications < loggedInUserInterval) {
          return;
        }
        lastNotifications = Date.now();
        this.loadNotifications().catch(() => {});
      };

      // Anonymous users dont have notifications so we still hit the heartbeat route here. 
      let lastKeepAlive = Date.now();
      const keepSessionAlive = () => {
        if (this.isUserLoggedIn) {
          return;
        }

        // Prevents notification spam if the user rapidly switches back and forth to this tab
        const due = document.hidden ? keepAliveIfNotActiveTab : keepAliveIfActiveTab;
        if (Date.now() - lastKeepAlive < due) {
          return;
        }
        lastKeepAlive = Date.now();

        axios
          .get(this.$apiUrl("heartbeat"))
          .catch(() => { logError("Heartbeat failed!"); });
      };

      setInterval(() => {
        refreshNotifications();
        keepSessionAlive();
      }, loggedInUserInterval);

      // On refocus, refresh so a long-backgrounded tab is up to date 
      document.addEventListener("visibilitychange", () => {
        if (document.hidden) {
          return;
        }
        refreshNotifications();
        keepSessionAlive();
      });
    },

    setupLibraryLogger() {
      TuLogger.setLogger({
        "logError": logError,
        "logInfo": logInfo,
        "logWarning": logWarning,
      });
    },

    setupCurrentSubscription() {
      if (this.isUserLoggedIn) {
        this.SET_CURRENT_SUBSCRIPTION(this.billable.currentSubscription);
      } else {
        this.SET_CURRENT_SUBSCRIPTION(null);
      }
    },

    async setupSessionPrint() {
      const fp = await getFingerprint();
      localStorage.setItem("sessionPrint", fp);
    },

    switchUILanguage(language) {
      i18n.i18next.changeLanguage(language);
    },

    switchMomentLanguage(language) {
      moment.locale(language);
    },

    redirectPastDueUsers(to) {
      let allowedPages = ["signup.pricing", "cookies", "privacy", "terms"];

      if (!this.isPastDueTrial) {
        allowedPages = [
          ...allowedPages,
          ...[
            "settings.profile",
            "settings.security",
            "settings.brandedDomains",
            "settings.brandedDomains.edit",
            "settings.api",
            "settings.api.edit",
            "settings.api.new",
            "shopping-cart.orders",
            "shopping-cart.orders.order",
            "settings.paymentMethod",
            "settings.invoices",
          ],
        ];
      }

      if (
        this.isPastDueTrial &&
        // this.getUserCurrentSubscription?.isPastDue &&
        !allowedPages.includes(to?.name)
      ) {
        this.$router.push({ name: "subscription.past_due" });
      }
    },
  },

  render: (h) => h(LaravelBladeVueBridge),
});

Vue.config.productionTip = false;
Vue.prototype.$Bus = Bus;
